import React, { useEffect, useState } from 'react'
import PageHeader from '../../Components/PageHeader/PageHeader'
import AppInput from '../../Components/AppInput/AppInput'
import { FileText, DollarSign, Calendar, AlignLeft } from 'lucide-react'
import AppSelect from '../../Components/AppSelect/AppSelect'
import { expenseCategoryOptions, paymentMethodOptions, reimbursementOptions } from '../../Constant/Options'
import { hasError } from '../../utils'
import { useDispatch, useSelector } from 'react-redux'
import AppButton from '../../Components/AppButton/AppButton'
import { ExpenseValidationScheema } from '../../ValidationScheema/ExpenseValidation'
import { setApiErrorJson, setApiJson } from '../../Redux/Actions/ApiAction'
import { ApiHit } from '../../Api/ApiHit'
import { addExpense, updateExpense, searchExpense } from '../../Constant/Constant'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import FileUpload from '../../Components/FileUpload/FileUpload'
import { getCahbookUser } from '../../Storage/Storage'

function AddExpense() {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get ID from URL
    const url = window.location.pathname;
    const urlId = url.split('/')[3];

    useEffect(() => {
        if (urlId) {
            setIsEditMode(true);
            loadExpenseData();
        } else {
            // Initialize with empty data for add mode
            const initialData = {
                title: "",
                amount: "",
                date: "",
                category: "",
                paymentMethod: "",
                reimbursement: "",
                description: "",
                proofUrl: ""
            };
            dispatch(setApiJson(initialData));
            dispatch(setApiErrorJson({}));
        }
    }, [urlId]);

    const loadExpenseData = async () => {
        try {
            setLoading(true);
            const json = {
                page: 1,
                limit: 10,
                search: {
                    _id: urlId
                }
            };

            const res = await ApiHit(json, searchExpense);

            if (res?.statusCode === 200) {
                const expenseData = res?.data?.docs?.[0];

                if (expenseData) {
                    // Convert timestamp to date format for input field
                    if (expenseData.date) {
                        const date = new Date(expenseData.date);
                        expenseData.date = date.toISOString().split('T')[0];
                    }
                    dispatch(setApiJson(expenseData));
                    dispatch(setApiErrorJson({}));
                } else {
                    toast.error('Expense not found');
                    navigate('/expense');
                }
            } else {
                toast.error('Failed to load expense data');
            }
        } catch (error) {
            console.error("Error loading expense:", error);
            toast.error('Error loading expense data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitLoading(true);

        try {
            const error = await ExpenseValidationScheema(ApiReducer?.apiJson, isEditMode);
            dispatch(setApiErrorJson(error));

            if (Object.keys(error).length === 0) {
                const apiEndpoint = isEditMode ? updateExpense : addExpense;
                const formData = { ...ApiReducer?.apiJson };

                if (formData.date) {
                    formData.date = new Date(formData.date).getTime();
                    formData.userId = getCahbookUser()?._id
                }

                const apiData = isEditMode
                    ? { ...formData, _id: urlId }
                    : formData;

                const res = await ApiHit(apiData, apiEndpoint);
                console.log("res", res);

                const successCode = isEditMode ? 200 : 201;
                if (res?.statusCode === successCode) {
                    toast.success(isEditMode ? 'Expense updated successfully' : 'Expense added successfully');
                    setTimeout(() => {
                        navigate("/expense");
                    }, 1000);
                } else {
                    toast.error(res?.error || 'Something went wrong');
                }
            }
        } catch (error) {
            console.error('Error submitting expense:', error);
            toast.error(`Error ${isEditMode ? 'updating' : 'creating'} expense. Please try again.`);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Show loading state while fetching data in edit mode
    if (loading && isEditMode) {
        return (
            <div className='p-5'>
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading expense data...</div>
                </div>
            </div>
        );
    }

    return (
        <div >
            <PageHeader
                title={isEditMode ? 'Edit Expense' : 'Add Expense'}
                description={isEditMode ? 'Update expense information and details' : 'Create and record a new expense entry in the system'}
            />

            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Expense Information</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <AppInput
                        title="Expense Title"
                        placeholder="Enter expense title"
                        name="title"
                        important={true}
                        error={hasError('title', ApiReducer)}
                        icon={<FileText size={18} />}
                        str
                        disabled={submitLoading}
                    />
                    <AppInput
                        title="Amount"
                        placeholder="Enter amount"
                        name="amount"
                        important={true}
                        error={hasError('amount', ApiReducer)}
                        icon={<DollarSign size={18} />}
                        type="number"
                        disabled={submitLoading}
                    />
                    <AppInput
                        title="Date"
                        placeholder="Select date"
                        name="date"
                        important={true}
                        error={hasError('date', ApiReducer)}
                        icon={<Calendar size={18} />}
                        type="date"
                        disabled={submitLoading}
                    />
                    <AppSelect
                        title="Category"
                        name="category"
                        options={expenseCategoryOptions}
                        important={true}
                        placeholder="Select category"
                        error={hasError('category', ApiReducer)}
                        disabled={submitLoading}
                    />
                    <AppSelect
                        title="Payment Method"
                        name="paymentMethod"
                        options={paymentMethodOptions}
                        important={true}
                        placeholder="Select payment method"
                        error={hasError('paymentMethod', ApiReducer)}
                        disabled={submitLoading}
                    />
                    <AppSelect
                        title="Reimbursement Required"
                        name="reimbursement"
                        options={reimbursementOptions}
                        important={true}
                        placeholder="Select option"
                        error={hasError('reimbursement', ApiReducer)}
                        disabled={submitLoading}
                    />
                </div>
                <div className="mb-6">
                    <AppInput
                        title="Description"
                        placeholder="Enter expense description"
                        name="description"
                        important={false}
                        error={hasError('description', ApiReducer)}
                        icon={<AlignLeft size={18} />}
                        str
                        disabled={submitLoading}
                    />
                </div>
                <div className="mb-6">
                    <FileUpload
                        title="Expense Proof"
                        name="proofUrl"
                        allowed={["img", "pdf"]}
                        disabled={submitLoading}
                        important={true}
                       error={hasError('proofUrl', ApiReducer)}
                    />
                </div>
                <div className="flex justify-end gap-3">
                    <AppButton
                        buttontext={submitLoading ? "Processing..." : (isEditMode ? "Update Expense" : "Add Expense")}
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitLoading}
                        isLoading={submitLoading}
                    />
                </div>
            </div>

        </div>
    )
}

export default AddExpense
